/* eslint-disable no-param-reassign, no-shadow */
import { EditorStore, Node } from '@candulabs/craft';
import isEqualWith from 'lodash/isEqualWith';

const isObject = (value: any): value is Record<string, any> =>
  value && typeof value === 'object' && value.constructor === Object;

export type RetrievedCommonValues<T, D> = {
  [P in keyof T]: Rest<T[P], D>;
};

type Rest<V, D> = V extends Record<string, any> ? RetrievedCommonValues<V, D> | D : V | D;
export function getCommonNodeProperties<C, D = null>(
  query: EditorStore['query'],
  nodeIds: string[],
  valuesToCheck: (node: Node) => C,
  placeholderValue?: D,
): RetrievedCommonValues<C, D> {
  let output = {};
  let lhs = {};

  const comparator = (
    current: Record<string, any>,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    lhs: Record<string, any>,
    rhs: Record<string, any>,
  ) => {
    const keys = new Set([...Object.keys(lhs), ...Object.keys(rhs)]);

    Array.from(keys).forEach((key) => {
      // If the value is an object, iterate and diff its properties
      if (isObject(lhs[key]) && isObject(rhs[key])) {
        current[key] = { ...lhs[key] };
        comparator(current[key], lhs[key], rhs[key]);
        return;
      }

      // If the values are different, set the final value for the key to null
      if (!isEqualWith(lhs[key], rhs[key])) {
        current[key] = placeholderValue;
        return;
      }

      current[key] = lhs[key];
    });
  };

  nodeIds.forEach((id, i) => {
    const node = query.node(id)?.get();
    if (!node) {
      return;
    }
    const obj = valuesToCheck(node);
    if (i === 0) {
      lhs = obj;
      output = { ...lhs };
      return;
    }

    comparator(output, lhs, obj);
    lhs = output;
  });

  return output as any;
}
